import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import UserLeaderBoardInfo from "./user-leader-board-info";
import LeaderBoardMenu from "./leader-board-menu";
import ShareModal from "./share-modal";
import { LeaderboardMetric, MenuType } from "@/types/leader-board";
import DropdownSearchInput from "@/components/atomics/dropdown-search-input";
import Button from "@/components/atomics/button";
import { LeaderboardItemType } from "@/types/leader-board";
import { useWallet } from "@/context/WalletContext";
import { backend } from "@/services/backend";
import { generateMiniAppCustomUrl } from "@/utils/telegram";
import {
  fetchOwnerNftData,
  fetchOwnerCollectionData,
  fetchOwnerUserData,
} from "@/utils/leaderboardDataFetcher";
import {
  OwnerData,
  OwnerCollectionData,
  OwnerNftData,
} from "@/types/collection";
import { fetchData as fetchJson } from "@/types/collection";
import {
  LEADERBOARD_CONSTANTS,
  LEADERBOARD_TEXT,
} from "@/constants/leaderboard";
import { ratingBackend } from "@/services/ratingBackend";

interface LeaderBoardShareProps {
  className?: string;
  userInfo: LeaderboardItemType;
  initialSelectedMenu: MenuType;
  initialMetric: LeaderboardMetric;
  onClickClose: () => void;
}

export default function LeaderBoardShare({
  className,
  userInfo,
  initialSelectedMenu,
  initialMetric,
  onClickClose,
}: LeaderBoardShareProps) {
  const [selectedMenu, setSelectedMenu] =
    useState<MenuType>(initialSelectedMenu);
  const [metric, setMetric] = useState<LeaderboardMetric>(initialMetric);
  const [showCopied, setShowCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentUserInfo, setCurrentUserInfo] =
    useState<LeaderboardItemType>(userInfo);
  const [dropdownItems, setDropdownItems] = useState<any[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const isInitialMount = useRef(true);
  const { walletAddress } = useWallet();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Simple functions
  const getShareTitle = () => {
    switch (initialSelectedMenu) {
      case "users":
        return "user share";
      case "collections":
        return "collection share";
      case "nft":
        return "nft share";
      default:
        return "share";
    }
  };

  const generateShareUrl = async () => {
    // Build path with order_param included: leaderboard/:type/:order_param or leaderboard/:order_param for collections
    const basePath =
      selectedMenu === "collections"
        ? `leaderboard/${metric}`
        : `leaderboard/${selectedMenu}/${metric}`;

    let referralLink = walletAddress;
    if (walletAddress) {
      try {
        const customLink =
          await backend.createCustomReferralLink(walletAddress);
        referralLink = customLink || walletAddress;
      } catch (error) {
        console.warn("Failed to create referral link:", error);
      }
    }

    // Only include share and address in query params - order_param is now in the path
    const queryParams: Record<string, string> = {
      share: "true",
      addr: currentUserInfo.address, // Just the address, data fetched on open
    };

    const miniAppUrl = generateMiniAppCustomUrl(
      basePath,
      referralLink || undefined,
      queryParams
    );

    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(miniAppUrl)}`;

    return shareUrl;
  };

  const handleShare = async () => {
    setIsLoading(true);
    try {
      const shareUrl = await generateShareUrl();

      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.openTelegramLink(shareUrl);
      } else {
        window.open(shareUrl, "_blank");
      }
    } catch (error) {
      enqueueSnackbar("Failed to share", {
        SnackbarProps: {
          onClick: () => {
            closeSnackbar();
          },
        },
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    setIsLoading(true);
    try {
      // Build path with order_param included
      const basePath =
        selectedMenu === "collections"
          ? `leaderboard/${metric}`
          : `leaderboard/${selectedMenu}/${metric}`;

      let referralLink = walletAddress;
      if (walletAddress) {
        try {
          const customLink =
            await backend.createCustomReferralLink(walletAddress);
          referralLink = customLink || walletAddress;
        } catch (error) {
          console.warn("Failed to create referral link:", error);
        }
      }

      const queryParams: Record<string, string> = {
        share: "true",
        addr: currentUserInfo.address,
      };

      // Generate direct mini app URL (not wrapped in share URL)
      const directUrl = generateMiniAppCustomUrl(
        basePath,
        referralLink || undefined,
        queryParams
      );

      await navigator.clipboard.writeText(directUrl);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
      enqueueSnackbar("Link copied to clipboard!", {
        SnackbarProps: {
          onClick: () => {
            closeSnackbar();
          },
        },
        variant: "success",
        autoHideDuration: 2000,
      });
    } catch (error) {
      enqueueSnackbar("Failed to copy link", {
        SnackbarProps: {
          onClick: () => {
            closeSnackbar();
          },
        },
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareToStory = async () => {
    setIsLoading(true);
    try {
      // Build path with order_param included: leaderboard/:type/:order_param or leaderboard/:order_param for collections
      const basePath =
        selectedMenu === "collections"
          ? `leaderboard/${metric}`
          : `leaderboard/${selectedMenu}/${metric}`;

      let referralLink = walletAddress;
      if (walletAddress) {
        try {
          const customLink =
            await backend.createCustomReferralLink(walletAddress);
          referralLink = customLink || walletAddress;
        } catch (error) {
          console.warn("Failed to create referral link:", error);
        }
      }

      // Only include share and address in query params - order_param is now in the path
      const queryParams: Record<string, string> = {
        share: "true",
        addr: currentUserInfo.address,
      };

      const miniAppUrl = generateMiniAppCustomUrl(
        basePath,
        referralLink || undefined,
        queryParams
      );

      // Use avatar as the story media (or a default image)
      const mediaUrl = currentUserInfo.avatar || "/assets/owner-avatar.png";

      if (window.Telegram?.WebApp?.shareToStory) {
        window.Telegram.WebApp.shareToStory(mediaUrl, {
          text: `Check out my position on the leaderboard!`,
          widget_link: {
            url: miniAppUrl,
            name: "Open Leaderboard",
          },
        });
      } else {
        enqueueSnackbar(
          "Stories are only available in Telegram app for Premium users",
          {
            SnackbarProps: {
              onClick: () => {
                closeSnackbar();
              },
            },
            variant: "warning",
            autoHideDuration: 3000,
          }
        );
      }
    } catch (error) {
      console.error("Error sharing to story:", error);
      enqueueSnackbar("Failed to share to story", {
        SnackbarProps: {
          onClick: () => {
            closeSnackbar();
          },
        },
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOwnerItems = useCallback(async () => {
    if (!userInfo.ownerAddress) return;

    setIsLoadingItems(true);
    try {
      const ownerData: OwnerData | null = await backend.getOwnerData(
        userInfo.ownerAddress,
        "user"
      );

      if (!ownerData) {
        setDropdownItems([]);
        return;
      }

      if (selectedMenu === "collections") {
        const collectionsWithMeta = await Promise.all(
          ownerData.collections.map(async collection => {
            try {
              const meta = await fetchJson(collection.data_url);
              return {
                address: collection.address,
                name: meta.name || LEADERBOARD_TEXT.UNKNOWN_NAME,
                avatar: meta.image || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
              };
            } catch (error) {
              return {
                address: collection.address,
                name: LEADERBOARD_TEXT.UNKNOWN_NAME,
                avatar: LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
              };
            }
          })
        );
        setDropdownItems(collectionsWithMeta);
      } else if (selectedMenu === "nft") {
        const nftsWithMeta = await Promise.all(
          ownerData.items.map(async nft => {
            try {
              const meta = await fetchJson(nft.data_url);
              return {
                address: nft.address,
                name: meta.name || LEADERBOARD_TEXT.UNKNOWN_NAME,
                avatar: meta.image || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
                indexInCollection: nft.index_in_collection,
              };
            } catch (error) {
              return {
                address: nft.address,
                name: LEADERBOARD_TEXT.UNKNOWN_NAME,
                avatar: LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
                indexInCollection: nft.index_in_collection,
              };
            }
          })
        );
        setDropdownItems(nftsWithMeta);
      } else {
        setDropdownItems([]);
      }
    } catch (error) {
      console.error("Error fetching owner items:", error);
      setDropdownItems([]);
    } finally {
      setIsLoadingItems(false);
    }
  }, [selectedMenu, userInfo.ownerAddress]);

  const fetchUpdatedUserInfo = useCallback(async () => {
    if (!userInfo.ownerAddress) return;

    try {
      let updatedInfo: LeaderboardItemType | null = null;

      if (selectedMenu === "nft") {
        updatedInfo = await fetchOwnerNftData(userInfo.ownerAddress);
      } else if (selectedMenu === "collections") {
        updatedInfo = await fetchOwnerCollectionData(
          userInfo.ownerAddress,
          metric
        );
      } else {
        updatedInfo = await fetchOwnerUserData(userInfo.ownerAddress, metric);
      }

      if (updatedInfo) {
        setCurrentUserInfo(updatedInfo);
      }
    } catch (error) {
      console.error("Error fetching updated user info:", error);
    }
  }, [selectedMenu, metric, userInfo.ownerAddress]);

  const handleDropdownItemSelect = useCallback(
    async (item: any) => {
      try {
        let updatedInfo: LeaderboardItemType | null = null;

        if (selectedMenu === "nft") {
          const nftData = await ratingBackend.getNftRatingData({
            address: item.address,
            param: "stars",
          });

          if (nftData) {
            updatedInfo = {
              rank: nftData.rang,
              name: item.name,
              value: Number(nftData.stars || 0),
              address: item.address,
              avatar: item.avatar,
              indexInCollection: item.indexInCollection,
              ownerAddress: userInfo.ownerAddress,
            };
          }
        } else if (selectedMenu === "collections") {
          const collectionData =
            await ratingBackend.getSingleCollectionRatingData({
              address: item.address,
            });

          if (collectionData) {
            const param = metric === "volume" ? "volume" : "stars";
            updatedInfo = {
              rank:
                metric === "volume"
                  ? collectionData.rangVolume || 0
                  : collectionData.rangStars || 0,
              name: item.name,
              value:
                metric === "volume"
                  ? Number(collectionData.volume || 0) /
                    LEADERBOARD_CONSTANTS.TON_DIVISOR
                  : Number(collectionData.stars || 0),
              address: item.address,
              avatar: item.avatar,
              ownerAddress: userInfo.ownerAddress,
            };
          }
        }

        if (updatedInfo) {
          setCurrentUserInfo(updatedInfo);
        }
      } catch (error) {
        console.error("Error fetching selected item data:", error);
      }
    },
    [selectedMenu, metric, userInfo.ownerAddress]
  );

  useEffect(() => {
    if (selectedMenu === "collections" || selectedMenu === "nft") {
      fetchOwnerItems();
    } else {
      setDropdownItems([]);
    }
  }, [selectedMenu, fetchOwnerItems]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (selectedMenu === initialSelectedMenu && metric === initialMetric) {
      setCurrentUserInfo(userInfo);
      return;
    }

    fetchUpdatedUserInfo();
  }, [
    selectedMenu,
    metric,
    initialSelectedMenu,
    initialMetric,
    fetchUpdatedUserInfo,
    userInfo,
  ]);

  return (
    <div className={`relative bg-black ${className}`}>
      <div className="flex justify-between items-center pl-[20px] pr-[20px] pt-[12px] pb-[12px] bg-[#0099FE]">
        <div className="text-[33px] text-uppercase">
          <span>share</span>
        </div>
        <div
          className="w-[29px] h-[29px] hover:cursor-pointer"
          onClick={onClickClose}
        >
          <img src="/assets/cancel.svg" alt="cancel" />
        </div>
      </div>

      <div className="flex flex-col px-[54px] py-[10px]">
        <span className="text-[24px] uppercase">{getShareTitle()}</span>
      </div>

      <UserLeaderBoardInfo
        className="!bg-[#2B2B2B]"
        circleColor="#2B2B2B"
        userInfo={currentUserInfo}
        selectedMenu={selectedMenu}
        metric={metric}
      />

      <div className="flex flex-col py-[27px]">
        <LeaderBoardMenu
          selectedMenu={selectedMenu}
          onMenuChange={setSelectedMenu}
          metric={metric}
          onMetricChange={setMetric}
        />

        {(selectedMenu === "collections" || selectedMenu === "nft") && (
          <DropdownSearchInput
            className="mx-[30px]"
            placeholder={
              selectedMenu === "collections"
                ? "Select a collection"
                : "Select an NFT"
            }
            items={dropdownItems}
            onSelect={handleDropdownItemSelect}
            isLoading={isLoadingItems}
          />
        )}

        <div className="pt-[40px] pb-[30px] px-[30px]">
          <div className="flex items-center justify-between gap-x-[20px]">
            <Button
              className="w-full h-[64px] text-[24px] hover:cursor-pointer hover:scale-102 transition-transform duration-300 ease-in-out disabled:opacity-50"
              image="/assets/button.svg"
              text="SHARE LINK"
              onClick={() => setShowShareModal(true)}
              disabled={isLoading}
            />
            <div className="relative">
              {showCopied && (
                <div className="absolute top-[-52px] right-[-20px] z-[12] text-white bg-black px-2 py-1 rounded text-sm">
                  Copied!
                </div>
              )}
              <div
                className="aspect-square h-[64px] hover:cursor-pointer hover:opacity-80"
                onClick={handleCopy}
              >
                <img
                  src="/assets/leaderboard-sharelink-copy.png"
                  alt="share link"
                />
              </div>
            </div>
          </div>
        </div>

        {showShareModal && (
          <div className="absolute inset-0 z-[100]">
            <div
              className="absolute inset-0 bg-black/70"
              onClick={() => setShowShareModal(false)}
            />
            <div className="absolute bottom-0 left-0 right-0">
              <ShareModal
                isLoading={isLoading}
                onClose={() => setShowShareModal(false)}
                onShareToStory={handleShareToStory}
                onShareToTelegram={handleShare}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
